import { io, Socket } from 'socket.io-client'
import config from '../config'
import { joinProjectRealtime, ProjectRealtimeSnapshot } from './api/realtime'

const url = config.wsUrl

type SnapshotHandler = (snapshot: ProjectRealtimeSnapshot, requestedAt: number) => void

const websocket = {
    joinedProjectIds: new Set<string>(),
    reconnectHandlerInstalled: false,
    snapshotHandlers: new Set<SnapshotHandler>(),
    socket: io(url, {
        autoConnect: false,
        transports: ['websocket'],
    }) as Socket,

    connect(token: string) {
        // Socket.IO позволяет выставить auth перед connect()
        this.socket.auth = { token }
        this.installReconnectHandler()
        if (!this.socket.connected) {
            this.socket.connect()
        }
    },

    installReconnectHandler() {
        if (this.reconnectHandlerInstalled) {
            return
        }

        this.reconnectHandlerInstalled = true

        this.socket.on('connect', () => {
            if (!this.joinedProjectIds.size) {
                return
            }

            this.syncProjects([...this.joinedProjectIds])
        })
    },

    onProjectSnapshot(handler: SnapshotHandler) {
        this.snapshotHandlers.add(handler)
    },

    async syncProjects(projectIds: string[]) {
        this.socket.emit('joinProjects', projectIds)

        await Promise.all(
            projectIds.map(async projectId => {
                try {
                    const requestedAt = Date.now()
                    const snapshot = await joinProjectRealtime(projectId)
                    if (!this.joinedProjectIds.has(projectId)) {
                        return
                    }
                    this.snapshotHandlers.forEach(handler => handler(snapshot, requestedAt))
                } catch (e) {
                    // Состояние останется из загруженного проекта, события продолжат приходить
                }
            })
        )
    },

    async joinProjects(projectIds: string[]) {
        const newProjectIds = projectIds.filter(projectId => !this.joinedProjectIds.has(projectId))
        newProjectIds.forEach(projectId => this.joinedProjectIds.add(projectId))

        // Без соединения в комнаты зайдёт обработчик connect
        if (!newProjectIds.length || !this.socket.connected) {
            return
        }

        await this.syncProjects(newProjectIds)
    },

    leaveProjects(projectIds: string[]) {
        projectIds.forEach(projectId => this.joinedProjectIds.delete(projectId))
        this.socket.emit('leaveProjects', projectIds)
    },

    joinProject(projectId: string) {
        return this.joinProjects([projectId])
    },

    leaveProject(projectId: string) {
        this.leaveProjects([projectId])
    },
}

export default websocket
